import React, { createContext, useContext, useState, useEffect } from "react";

const DashboardContext = createContext();

export function DashboardProvider({
  children,
  storageKey = "react-dashboard-layouts:v1",
  initialLayouts = { lg: [], md: [], sm: [], xs: [], xxs: [] },
  initialWidgets = []
}) {
  const [widgets, setWidgets] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw).widgets : initialWidgets;
    } catch (e) {
      return initialWidgets;
    }
  });

  const [layouts, setLayouts] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw).layouts : initialLayouts;
    } catch (e) {
      return initialLayouts;
    }
  });

  // persist
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ layouts, widgets }));
    } catch (e) {
      // noop
    }
  }, [layouts, widgets, storageKey]);

  // Add a widget (widget: { id, component }, layoutDefaults: { w,h,x,y })
  const addWidget = (widget, layoutDefaults = { w: 3, h: 2 }) => {
    setWidgets((w) => [...w, widget]);
    setLayouts((ls) => {
      const newLayouts = { ...ls };
      Object.keys(newLayouts).forEach((bp) => {
        newLayouts[bp] = [...(newLayouts[bp] || []), {
          i: widget.id,
          x: layoutDefaults.x ?? 0,
          y: layoutDefaults.y ?? Infinity,
          w: layoutDefaults.w,
          h: layoutDefaults.h
        }];
      });
      return newLayouts;
    });
  };

  const removeWidget = (id) => {
    setWidgets((w) => w.filter((x) => x.id !== id));
    setLayouts((ls) => {
      const newLayouts = {};
      Object.keys(ls).forEach((bp) => {
        newLayouts[bp] = (ls[bp] || []).filter((L) => L.i !== id);
      });
      return newLayouts;
    });
  };

  const updateLayouts = (newLayouts) => setLayouts(newLayouts);

  return (
    <DashboardContext.Provider value={{
      widgets,
      layouts,
      addWidget,
      removeWidget,
      updateLayouts,
    }}>
      {children}
    </DashboardContext.Provider>
  );
}

export const useDashboard = () => {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error("useDashboard must be used within DashboardProvider");
  return ctx;
};
